import {useCallback, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {getUsersAudit} from 'src/openapi/admin/apis/AuditControllerApi';
import {useAppDispatch} from 'src/store/hooks';

import {
    userSearchResultAuditActions,
    userSearchResultAuditDataSelector,
    userSearchResultAuditResultSelector,
} from '../slices/user-search-result-audit.slice';
import {selectSelectedColumns} from '../slices/user-search-result-columns.slice';
import {userSearchResultStateSelector} from '../slices/user-search-result-state.slice';
import {checkShouldLoadColumn} from '../utils';

import type {UserSearchResultCacheKeys} from '../typedef';
import type {UserAudit} from 'src/openapi/admin/models/UserAudit';


type Props = {
    userIds: number[],
    totalCount: number,
    columnId: string,
};

export const useGetUserSearchResultAudit = ({userIds, totalCount, columnId}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();
    const selectedColumns = useSelector(selectSelectedColumns);

    const getAction = useCallback(() => apiDispatch(getUsersAudit({
        requestBody: userIds,
    })), [userIds, apiDispatch]);

    const setLoadedData = useCallback((
        response: Record<string, UserAudit>,
        dataCacheKeys: UserSearchResultCacheKeys,
    ) => {
        dispatch(userSearchResultAuditActions.onSuccess(({
            cacheKeys: dataCacheKeys,
            totalCount,
            data: response,
        })));
    }, [totalCount, dispatch]);

    const shouldUpdateData = useMemo(
        () => checkShouldLoadColumn(columnId, selectedColumns),
        [columnId, selectedColumns],
    );

    return useCachedTable({
        dataSelector: userSearchResultAuditDataSelector,
        stateSelector: userSearchResultStateSelector,
        resultSelector: userSearchResultAuditResultSelector,
        actions: userSearchResultAuditActions,
        getAction,
        onSuccessLoad: setLoadedData,
        shouldClearOnUnmount: false,
        shouldUpdateData,
    });
};
